// Même présentation que modules/formatting/contact-details.php pour les champs de saisie.
export function formatCity(value) {
  const particles = ['de', 'du', 'des', 'au', 'aux', 'à', 'la', 'le', 'les', 'sur', 'sous', 'en', 'et', 'd', 'l'];
  let wordIndex = 0;
  return value.trim().replace(/\p{L}[\p{L}\p{M}]*/gu, (word) => {
    const lower = word.toLowerCase();
    if (wordIndex++ > 0 && particles.includes(lower)) return lower;
    const original = word === word.toUpperCase() ? lower : word;
    return original.replace(/^\p{L}/u, (initial) => initial.toUpperCase());
  });
}

export function formatPhone(value) {
  const raw = value.trim();
  const extensionMatch = raw.match(/\s*(?:poste|ext(?:ension)?\.?|x|#)\s*[:.]?\s*(\d+)\s*$/iu);
  const number = extensionMatch ? raw.slice(0, extensionMatch.index).trim() : raw;
  if (!/^\+?[\d\s().-]+$/u.test(number)) return raw;
  let digits = number.replace(/\D/g, '');
  const hasCountryCode = digits.length === 11 && digits[0] === '1';
  if (hasCountryCode) digits = digits.slice(1);
  if (digits.length !== 10 || (!hasCountryCode && number.includes('+'))) return raw;
  const display = `${hasCountryCode ? '+1 ' : ''}${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return display + (extensionMatch ? ` poste ${extensionMatch[1]}` : '');
}

export function limitPhoneDigits(value) {
  let digits = 0;
  for (let index = 0; index < value.length; index++) {
    if (value[index] >= '0' && value[index] <= '9' && ++digits > 10) {
      return value.slice(0, index);
    }
  }
  return value;
}

function isPhoneInput(input) {
  return input && typeof input.matches === 'function' &&
    input.matches('input[type="text"], input[type="tel"], input:not([type])') &&
    (input.dataset.ccContactFormat === 'phone' || ['user_phone', 'company_phone', 'billing_phone', 'bphone'].includes(input.name));
}

export function formatPhoneTyping(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return digits.slice(0, 3) + (digits.length > 3 ? ` ${digits.slice(3, 6)}` : '') +
    (digits.length > 6 ? `-${digits.slice(6)}` : '');
}

function limitPhoneInput(input) {
  if (!isPhoneInput(input) || input.disabled || input.readOnly) return;
  const raw = input.value;
  const limited = formatPhoneTyping(raw);
  if (limited === input.value) return;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  input.value = limited;
  if (start !== null && end !== null) {
    const position = (offset) => {
      const count = Math.min((raw.slice(0, offset).match(/\d/g) || []).length, 10);
      if (!count) return 0;
      let seen = 0;
      for (let index = 0; index < limited.length; index++) {
        if (/\d/.test(limited[index]) && ++seen === count) return index + 1;
      }
      return limited.length;
    };
    input.setSelectionRange(position(start), position(end));
  }
}

function formatInput(input) {
  if (!input || !input.matches('input[type="text"], input[type="tel"], input:not([type])')) return;
  const format = input.dataset.ccContactFormat;
  if (format === 'city' || ['user_city', 'billing_city', 'project_city'].includes(input.name) || input.matches('.cc-mapbox-city-input, .cc-mapbox-city input')) {
    input.value = formatCity(input.value);
  } else if (format === 'phone' || ['user_phone', 'company_phone', 'billing_phone', 'bphone'].includes(input.name)) {
    input.value = formatPhone(input.value);
  }
}

function formatInputs(root) {
  root.querySelectorAll('input').forEach(formatInput);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => formatInputs(document));
  } else {
    formatInputs(document);
  }
  // Compter les chiffres; les espaces et tirets du format affiché ne comptent pas.
  document.addEventListener('beforeinput', (event) => {
    const input = event.target;
    if (!isPhoneInput(input) || input.disabled || input.readOnly || event.isComposing) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    // Éviter qu'un séparateur supprimé réapparaisse sans supprimer le chiffre adjacent.
    if (start === end && ['deleteContentBackward', 'deleteContentForward'].includes(event.inputType)) {
      const backward = event.inputType === 'deleteContentBackward';
      let index = backward ? start - 1 : start;
      if (index >= 0 && index < input.value.length && /[ -]/.test(input.value[index])) {
        while (index >= 0 && index < input.value.length && !/\d/.test(input.value[index])) index += backward ? -1 : 1;
        if (index >= 0 && index < input.value.length) {
          event.preventDefault();
          input.setRangeText('', index, index + 1, 'start');
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      return;
    }
    if (event.inputType !== 'insertText' || !event.data) return;
    if (!/^\d+$/.test(event.data)) {
      event.preventDefault();
      return;
    }
    const candidate = input.value.slice(0, start) + event.data + input.value.slice(end);
    if (/\d/.test(event.data) && (candidate.match(/\d/g) || []).length > 10) {
      event.preventDefault();
    }
  }, true);
  // Couvre aussi le collage et les navigateurs qui n'utilisent pas beforeinput.
  document.addEventListener('input', (event) => {
    if (!event.isComposing) limitPhoneInput(event.target);
  }, true);
  document.addEventListener('compositionend', (event) => limitPhoneInput(event.target), true);
  // Attendre la fin de la saisie pour préserver le curseur et l'autocomplétion Mapbox.
  document.addEventListener('blur', (event) => formatInput(event.target), true);
  document.addEventListener('change', (event) => formatInput(event.target), true);
  document.addEventListener('submit', (event) => formatInputs(event.target), true);
}
